import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { startQrScanner, stopQrScanner } from "@/lib/qrScanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Camera, CameraOff, ScanLine, UtensilsCrossed, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "");

const ScanResult = ({ result }) => {
  if (!result) return null;
  const dup = result.duplicate;
  return (
    <div className={`rounded-2xl border-2 p-5 ${dup ? "bg-orange-50 border-orange-400" : "bg-green-50 border-green-400"}`} data-testid="cantine-scan-result">
      <div className={`flex items-center gap-2 font-extrabold text-xl ${dup ? "text-orange-700" : "text-green-700"}`}>
        {dup ? <AlertTriangle className="h-6 w-6" /> : <CheckCircle2 className="h-6 w-6" />}
        {dup ? "REPAS DÉJÀ SERVI AUJOURD'HUI" : "REPAS ENREGISTRÉ"}
      </div>
      <div className="mt-2 text-lg font-bold text-slate-900">{result.student_name}</div>
      <div className="text-sm text-slate-600">{result.class_name} · {result.matricule} · {fmtTime(result.served_at)}</div>
      {result.subscribed ? (
        <div className="mt-1 text-xs font-semibold text-green-700">Abonnement cantine actif ✓ {result.regime ? `(régime ${result.regime})` : ""}</div>
      ) : (
        <div className="mt-1 text-xs font-semibold text-red-700 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" /> Aucun abonnement cantine actif — repas à facturer.
        </div>
      )}
    </div>
  );
};

export default function CantineScanTab() {
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [scans, setScans] = useState({ count: 0, meals: [] });
  const qrRef = useRef(null);
  const coolRef = useRef(0);

  const loadScans = async () => {
    try {
      const { data } = await api.get("/canteen/scans");
      setScans(data);
    } catch { /* silencieux */ }
  };
  useEffect(() => { loadScans(); }, []);

  const doScan = async (code) => {
    if (busy || !code) return;
    setBusy(true);
    try {
      const { data } = await api.post("/canteen/scan", { code });
      setResult(data);
      if (data.duplicate) toast.warning(`${data.student_name} a déjà été servi(e) aujourd'hui`);
      else toast.success(`Repas enregistré : ${data.student_name} ✓`);
      loadScans();
    } catch (e) {
      setResult(null);
      toast.error(e.response?.data?.detail || "Erreur de scan");
    } finally { setBusy(false); }
  };

  const startCamera = async () => {
    const onDecoded = (text) => {
      const now = Date.now();
      if (now - coolRef.current < 3000) return;
      coolRef.current = now;
      doScan(text);
    };
    try {
      qrRef.current = await startQrScanner("cantine-qr-reader", onDecoded);
      setScanning(true);
    } catch (e) {
      toast.error(e.message, { duration: 8000 });
    }
  };

  const stopCamera = async () => {
    await stopQrScanner(qrRef.current);
    setScanning(false);
  };
  useEffect(() => () => { stopQrScanner(qrRef.current); }, []);

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="cantine-scan-tab">
      <div className="space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading font-bold text-[#1E3A8A] flex items-center gap-2"><Camera className="h-4 w-4" />Caméra QR — badges élèves</div>
            {scanning ? (
              <Button size="sm" variant="outline" onClick={stopCamera} className="text-red-600 border-red-300" data-testid="cantine-stop-camera"><CameraOff className="h-4 w-4 mr-1" />Arrêter</Button>
            ) : (
              <Button size="sm" onClick={startCamera} className="bg-[#1E3A8A] text-white" data-testid="cantine-start-camera"><Camera className="h-4 w-4 mr-1" />Démarrer</Button>
            )}
          </div>
          <div id="cantine-qr-reader" className="rounded-xl overflow-hidden bg-slate-900 min-h-[120px]" />
          <p className="text-[11px] text-slate-400 mt-2">Scannez le même badge QR que celui utilisé au portail (carte scolaire ou QR élève).</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <div className="font-heading font-bold text-[#1E3A8A] flex items-center gap-2 mb-3"><ScanLine className="h-4 w-4" />Saisie manuelle</div>
          <form onSubmit={(e) => { e.preventDefault(); doScan(manual.trim()); setManual(""); }} className="flex gap-2">
            <Input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Matricule ou code badge" data-testid="cantine-manual-input" />
            <Button type="submit" disabled={busy || !manual.trim()} className="bg-[#EA580C] text-white" data-testid="cantine-manual-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Valider"}
            </Button>
          </form>
        </div>
        <ScanResult result={result} />
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="font-heading font-bold text-[#1E3A8A] flex items-center gap-2">
            <UtensilsCrossed className="h-4 w-4" /> Repas servis aujourd&apos;hui
          </div>
          <span className="text-sm font-extrabold text-[#166534] tabular-nums" data-testid="cantine-scans-count">{scans.count}</span>
        </div>
        <div className="max-h-[480px] overflow-y-auto divide-y divide-slate-100">
          {scans.meals.map((m) => (
            <div key={m.id || m.student_id} className="px-4 py-2.5 flex items-center justify-between text-sm" data-testid={`cantine-meal-${m.student_id}`}>
              <div>
                <div className="font-semibold text-slate-800">{m.student_name || m.student_id}</div>
                <div className="text-xs text-slate-500">{m.class_name}{m.matricule ? ` · ${m.matricule}` : ""}</div>
              </div>
              <div className="text-xs text-slate-400">{fmtTime(m.served_at || m.created_at)}</div>
            </div>
          ))}
          {scans.meals.length === 0 && <div className="px-4 py-10 text-center text-slate-400 text-sm">Aucun repas servi aujourd&apos;hui.</div>}
        </div>
      </div>
    </div>
  );
}
